import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Star, CheckCircle, PlayCircle, ArrowRight, Award, TrendingUp } from 'lucide-react';
import Button from '../../../components/ui/Button';

const RoadmapCard = ({ 
  roadmap, 
  onSelect, 
  isSelected = false, 
  onEnroll, 
  featured = false 
}) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-600 bg-green-100';
      case 'Intermediate': return 'text-blue-600 bg-blue-100';
      case 'Advanced': return 'text-purple-600 bg-purple-100';
      case 'Expert': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <motion.div
      className={`relative bg-card border rounded-xl p-6 transition-all duration-300 hover:shadow-lg group ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-border hover:border-blue-300'
      } ${featured ? 'border-yellow-300' : ''}`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -5 }}
    >
      {/* Featured Badge */}
      {featured && (
        <div className="absolute -top-3 -right-3 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
          <Star className="w-3 h-3" />
          <span>Featured</span>
        </div>
      )}
      {/* Trending Badge */}
      {roadmap?.trending && (
        <div className="absolute -top-3 -left-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
          <TrendingUp className="w-3 h-3" />
          <span>Trending</span>
        </div>
      )}
      {/* Header with Icon and Title */}
      <div className="flex items-start space-x-4 mb-4">
        <div className={`w-12 h-12 ${roadmap?.color} rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform`}>
          {roadmap?.icon}
        </div>
        
        <div className="flex-1">
          <h3 className="text-lg font-bold mb-1 group-hover:text-blue-600 transition-colors">
            {roadmap?.title}
          </h3>
          
          <div className="flex items-center space-x-3">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(roadmap?.difficulty)}`}>
              {roadmap?.difficulty}
            </span>
            <span className="text-sm text-muted-foreground flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>{roadmap?.estimatedTime}</span>
            </span>
          </div>
        </div>

        {/* Selection Checkbox */}
        <button
          onClick={(e) => {
            e?.stopPropagation();
            onSelect(roadmap);
          }}
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
            isSelected 
              ? 'border-blue-500 bg-blue-500' :'border-gray-300 hover:border-blue-400'
          }`}
        >
          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
        </button>
      </div>
      {/* Description */}
      <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
        {roadmap?.description}
      </p>
      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 mb-4 py-3 border-t border-border">
        <div className="text-center">
          <div className="text-sm font-semibold">{roadmap?.missions}</div>
          <div className="text-xs text-muted-foreground">Missions</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-semibold">{roadmap?.enrolled?.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">Enrolled</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-semibold flex items-center justify-center space-x-1">
            <Star className="w-3 h-3 text-yellow-500" />
            <span>{roadmap?.rating}</span>
          </div>
          <div className="text-xs text-muted-foreground">Rating</div>
        </div>
      </div>
      {/* Technologies/Prerequisites */}
      <div className="mb-4">
        <div className="text-xs font-medium text-muted-foreground mb-2">Technology:</div>
        <span className="text-xs bg-muted px-2 py-1 rounded-full">
          {roadmap?.technology}
        </span>
      </div>
      {/* Key Outcomes */}
      <div className="mb-4">
        <div className="text-xs font-medium text-muted-foreground mb-2">You'll Learn:</div>
        <div className="space-y-1">
          {roadmap?.outcomes?.slice(0, 3)?.map((outcome, index) => (
            <div key={index} className="flex items-center space-x-2 text-xs">
              <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
              <span className="text-muted-foreground">{outcome}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Certifications */}
      {roadmap?.certifications?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-medium text-muted-foreground mb-2 flex items-center space-x-1">
            <Award className="w-3 h-3" />
            <span>Certifications:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {roadmap?.certifications?.slice(0, 2)?.map((cert, index) => (
              <span key={index} className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                {cert}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex space-x-3">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 flex items-center justify-center space-x-2"
          onClick={(e) => {
            e?.stopPropagation();
            // Navigate to roadmap preview/details
          }}
        >
          <PlayCircle className="w-4 h-4" />
          <span>Preview</span>
        </Button>
        
        <Button
          size="sm"
          className="flex-1 flex items-center justify-center space-x-2"
          onClick={(e) => {
            e?.stopPropagation();
            onEnroll(roadmap?.slug);
          }}
        >
          <span>Start Learning</span>
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
      {/* Progress Bar (if user is enrolled) */}
      {roadmap?.userProgress && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold">{roadmap?.userProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${roadmap?.userProgress}%` }}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RoadmapCard;